import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

function PosterCard({
  label,
  title,
  body,
  bottomText,
  rotation,
  isFront,
  className,
}: {
  label: string;
  title: string;
  body: string;
  bottomText: string;
  rotation: string;
  isFront: boolean;
  className?: string;
}) {
  return (
    <div
      className={`absolute transition-all duration-700 ease-out ${className ?? ""}`}
      style={{
        width: 260,
        height: 460,
        transform: `${rotation} scale(${isFront ? 1.05 : 0.95})`,
        zIndex: isFront ? 2 : 1,
        opacity: isFront ? 1 : 0.85,
        boxShadow: "0 8px 40px rgba(0,0,0,0.35)",
        borderRadius: 4,
        background: "#0a0a0a",
        color: "#fff",
      }}
    >
      <div className="flex flex-col justify-between h-full px-6 py-8">
        <p
          style={{
            fontSize: 12,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            opacity: 0.6,
            fontWeight: 600,
          }}
        >
          {label}
        </p>

        <div className="flex-1 flex flex-col justify-center gap-4">
          <h3
            style={{
              fontSize: 38,
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            {title}
          </h3>
          <p
            style={{
              fontSize: 14.5,
              lineHeight: 1.65,
              opacity: 0.6,
              whiteSpace: "pre-line",
            }}
          >
            {body}
          </p>
        </div>

        <div>
          <div
            style={{
              height: 1,
              background: "rgba(255,255,255,0.15)",
              marginBottom: 10,
            }}
          />
          <p style={{ fontSize: 11, opacity: 0.45, letterSpacing: "0.1em" }}>
            {bottomText}
          </p>
        </div>
      </div>
    </div>
  );
}

export function VintageMac() {
  const { t } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const [swapped, setSwapped] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = 1 - rect.top / vh;
      setSwapped(progress > 0.55);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const matchBody = t("solution.match.screen");
  const createBody = t("solution.create.screen");

  return (
    <div ref={containerRef} className="mt-16 md:mt-24">
      {/* Desktop: fanned cards */}
      <div className="hidden md:flex justify-center items-center" style={{ height: 520 }}>
        <div className="relative" style={{ width: 520, height: 460 }}>
          <PosterCard
            label="SPONSORSHIP"
            title="Match"
            body={matchBody}
            bottomText={t("solution.bottom")}
            rotation="rotate(-5deg)"
            isFront={!swapped}
            className="left-0 top-0"
          />
          <PosterCard
            label="ORIGINAL IP"
            title="Create"
            body={createBody}
            bottomText={t("solution.bottom")}
            rotation="rotate(5deg)"
            isFront={swapped}
            className="left-[220px] top-[10px]"
          />
        </div>
      </div>

      {/* Mobile: stacked sequential */}
      <div className="flex md:hidden flex-col items-center gap-6 px-4">
        {[
          { label: "SPONSORSHIP", title: "Match", body: matchBody },
          { label: "ORIGINAL IP", title: "Create", body: createBody },
        ].map((card) => (
          <div
            key={card.label}
            style={{
              width: "100%",
              maxWidth: 280,
              background: "#0a0a0a",
              borderRadius: 4,
              color: "#fff",
              boxShadow: "0 6px 30px rgba(0,0,0,0.3)",
            }}
          >
            <div className="flex flex-col justify-between h-full px-5 py-7">
              <p style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", opacity: 0.6, fontWeight: 600 }}>
                {card.label}
              </p>
              <div className="flex-1 flex flex-col justify-center gap-3">
                <h3 style={{ fontSize: 32, fontWeight: 700, lineHeight: 1.05 }}>{card.title}</h3>
                <p style={{ fontSize: 13.5, lineHeight: 1.6, opacity: 0.6, whiteSpace: "pre-line" }}>{card.body}</p>
              </div>
              <div>
                <div style={{ height: 1, background: "rgba(255,255,255,0.15)", marginBottom: 8 }} />
                <p style={{ fontSize: 10, opacity: 0.4, letterSpacing: "0.1em" }}>{t("solution.bottom")}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
